import { CustomModal } from 'components/molecules/Modal/Modal'
import { FC } from 'react'
import { TSpecialRequestModalProps } from './SpecialRequestModal.types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Typography,
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import './SpecialRequestModal.scss'
import { useSpecialRequestModalLogic } from './SpecialRequestModal.logic'
import { CompanySearchBar } from 'components/CompanySearchBar'
import classNames from 'classnames'
import { Product } from './Product'
import { Notes } from './Notes'
import Warning from 'components/molecules/Warning/Warning'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import { Option } from 'common/types'
import { Rep } from './Rep'
import { Instruments } from './Instruments'

export const SpecialRequestModal: FC<TSpecialRequestModalProps> = (props) => {
  const { onClose } = props

  const logic = useSpecialRequestModalLogic(props)

  return (
    <CustomModal
      isLoadingState={logic.loadingCreateSpecialRequest}
      header="Special Requests"
      primaryButtonText="Create"
      open={true}
      secondaryButtonAction={onClose}
      handleOnSubmit={logic.handleCreateSpecialRequest}
      primaryButtonDisabled={logic.disableCreateSpecialRequest}
    >
      <div className="special-request-modal">
        <Accordion
          id="instruments"
          className="accordion"
          expanded={logic.isAccordionsElementOpen.instruments}
          onChange={(_, expanded) =>
            logic.handleAccordionChange('instruments', expanded)
          }
        >
          <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
            Instruments
          </AccordionSummary>

          <AccordionDetails
            id="instrumentTraysDetails"
            className="accordion-details"
          >
            <Instruments
              onChange={(selectedOptions) =>
                logic.handleInstrumentsChange('instruments', selectedOptions)
              }
              selectedInstruments={logic.instruments?.instruments || []}
            />
            <Divider />
            <Notes
              onNotesChange={(notes) =>
                logic.handleInstrumentsChange('notes', notes)
              }
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="implantsBiologics"
          className="accordion"
          expanded={logic.isAccordionsElementOpen.implantsBiologics}
          onChange={(_, expanded) =>
            logic.handleAccordionChange('implantsBiologics', expanded)
          }
        >
          <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
            Implants & Biologics
          </AccordionSummary>

          <AccordionDetails className="accordion-details">
            <Typography variant="h2">Add Companies</Typography>
            <CompanySearchBar
              required={false}
              variant="outlined"
              placeholder="Search Company"
              handleChangeFormData={logic.handleAddCompany}
            />
            <div
              className={classNames('companies-container', {
                disabled: logic.selectedCompanies.length === 0,
              })}
            >
              {logic.selectedCompanies.length > 0 ? (
                <div className="selected-companies">
                  {logic.selectedCompanies.map((item) => (
                    <Product
                      item={item}
                      key={item.id}
                      onDelete={logic.handleDeleteCompany}
                    />
                  ))}
                </div>
              ) : (
                <>No Companies added yet.</>
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion
          id="salesReps"
          className="accordion"
          expanded={logic.isAccordionsElementOpen.salesReps}
          onChange={(_, expanded) =>
            logic.handleAccordionChange('salesReps', expanded)
          }
        >
          <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
            Sales Representatives
          </AccordionSummary>

          <AccordionDetails className="accordion-details">
            {logic.selectedCompanies.length === 0 && (
              <Warning
                type="info"
                text="Please add companies for implants or biologics to show the vendors."
              />
            )}
            <Typography variant="h3">Implants & Biologics</Typography>
            <AutocompleteInput
              variant="outlined"
              label="Select Vendor"
              placeholder="Select Vendor"
              value={logic.selectedVendor}
              options={logic.selectedCompanies}
              disabled={!logic.selectedCompanies.length}
              handleChangeFormData={(e) =>
                logic.setSelectedVendor(e.target.value as Option)
              }
            />
            <AutocompleteInput
              showEmail
              sx={{ mt: 0 }}
              variant="outlined"
              label="Search Sales Reps"
              placeholder="Search Sales Reps"
              options={logic.salesRepsOptions}
              handleChangeFormData={logic.handleAddSalesRep}
              disabled={
                !logic.selectedCompanies.length || logic.loadingSalesReps
              }
            />
            <div className="selected-companies-container">
              {Object.entries(logic.groupedRepsByCompany).map(
                ([companyName, reps]) => (
                  <div key={companyName} className="company-group">
                    <Typography variant="h3">{companyName}</Typography>
                    {reps.map((rep) => (
                      <Rep
                        rep={rep}
                        key={rep?.auth0Id}
                        isLoading={false}
                        onNotifySurgeryToRep={logic.handleNotifySurgeryToRep}
                        onDelete={logic.handleDeleteSalesRep}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </CustomModal>
  )
}
